'use client';

import React, { ComponentType } from 'react';
import { RootState } from '@/store';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { hideModal, updateData } from '@/store/slices/modalSlice';
import Contact from '../../../features/contact/components/modals/Contact';
import RateTool from '@/features/tool/components/modals/RateTool';
import YourSign from '@/features/tool/components/modals/YourSign';
import Language from './Language';
import CompanyModal from '@/features/subscription/components/modals/CompanyModal';
import ChangePassword from '@/features/auth/components/Modals/ChangePassword';
import DeleteAccount from '@/features/auth/components/Modals/DeleteAccount';
import ProfileModal from '@/features/auth/components/Modals/ProfileModal';
import AddUserModal from '@/features/subscription/components/modals/AddUserModal';
import CancelSubscription from '@/features/subscription/components/modals/cancel-sub/CancelSubscription';
import UpgradeAnnual from '@/features/subscription/components/modals/UpgradeAnnual';
import EsignDeletionQuestion from '@/features/tool/components/modals/EsignDeletionQuestion';
import PremiumModal from '@/features/subscription/components/modals/PremiumModal';
import EditEmailModal from '@/features/auth/components/Modals/EditEmailModal';
import Rename from '@/features/tool/components/modals/Rename';
import MakeSure from './General/MakeSure';
import DownloadSignup from '@/features/tool/components/modals/DownloadSignupModal';
import DownloadVerificationCode from '@/features/tool/components/modals/DownloadVerificationCodeModal';
import DownloadLogin from '@/features/tool/components/modals/DownloadLoginModal';
import PlanSelection from '@/features/tool/components/modals/PlanSelectionModal';
import PaddlePayment from '@/features/tool/components/modals/PaddlePaymentModal';
import Paywall1 from '@/features/tool/components/modals/Paywall1Modal';
import Paywall2 from '@/features/tool/components/modals/Paywall2Modal';
import Paywall3 from '@/features/tool/components/modals/Paywall3Modal';
import DownloadSuccess from '@/features/tool/components/modals/DownloadSuccessModal';
import EditShare from '@/features/tool/components/modals/EditShareModal';
import EditProcessAnimation from '@/features/tool/components/modals/EditProcessAnimationModal';
import ProtectPdfPassword from '@/features/tool/components/modals/protect/ProtectPdfPasswordModal';
import ProtectPdfCurrentPassword from '@/features/tool/components/modals/protect/ProtectPdfCurrentPasswordModal';
import ProtectPdfProtected from '@/features/tool/components/modals/protect/ProtectPdfProtectedModal';
import UnlockPdfProcessing from '@/features/tool/components/modals/unlock/UnlockPdfProcessingModal';
import ReviewFlow from '@/features/review-flow/components/ReviewFlowModal';
import FallbackProgress from './FallbackProgressModal';
import ProceedPopup from '@/features/documents/components/ProceedPopupModal';
import { useInAppPaywallDesign } from '@/features/subscription/remote-config/useInAppPaywallDesign';
import { useFullScreenPaywallEnabled } from '@/features/subscription/remote-config/useFullScreenPaywallEnabled';

const PaddlePaymentEntry = (props: any) => {
  const design = useInAppPaywallDesign();
  const isFullScreen = useFullScreenPaywallEnabled();

  if (design === null) {
    return null;
  }

  if (design === 0) {
    return <PaddlePayment {...props} />;
  }

  if (isFullScreen === null) {
    return null;
  }

  const paywallProps = { ...props, data: { ...props.data, isFullScreen } };

  if (design === 2) {
    return <Paywall2 {...paywallProps} />;
  }

  if (design === 3) {
    return <Paywall3 {...paywallProps} />;
  }

  return <Paywall1 {...paywallProps} />;
};

const modalMap = {
  Contact: Contact,
  RateTool: RateTool,
  YourSign: YourSign,
  Language: Language,
  CompanyModal: CompanyModal,
  ChangePassword: ChangePassword,
  DeleteAccount: DeleteAccount,
  ProfileModal: ProfileModal,
  AddUserModal: AddUserModal,
  CancelSubscription: CancelSubscription,
  UpgradeAnnual: UpgradeAnnual,
  EsignDeletionQuestion: EsignDeletionQuestion,
  PremiumModal: PremiumModal,
  EditEmailModal: EditEmailModal,
  Rename: Rename,
  MakeSure: MakeSure,
  DownloadSignup: DownloadSignup,
  DownloadVerificationCode: DownloadVerificationCode,
  DownloadLogin: DownloadLogin,
  PlanSelection: PlanSelection,
  PaddlePayment: PaddlePaymentEntry,
  Paywall1: Paywall1,
  Paywall2: Paywall2,
  Paywall3: Paywall3,
  DownloadSuccess: DownloadSuccess,
  EditShare: EditShare,
  EditProcessAnimation: EditProcessAnimation,
  ProtectPdfPassword: ProtectPdfPassword,
  ProtectPdfCurrentPassword: ProtectPdfCurrentPassword,
  ProtectPdfProtected: ProtectPdfProtected,
  UnlockPdfProcessing: UnlockPdfProcessing,
  ReviewFlow: ReviewFlow,
  FallbackProgress: FallbackProgress,
  ProceedPopup: ProceedPopup,
};

interface ModalComponentProps {
  data: any;
  onCallback: (key?: string, data?: any) => void;
  hideModal: (data: any) => void;
}

const ModalRoot = () => {
  const dispatch = useAppDispatch();
  const { activeModal, modals } = useAppSelector(
    (state: RootState) => state.modal
  );

  const modal = activeModal ? modals[activeModal] : null;
  const ModalComponent = activeModal
    ? (modalMap[
        activeModal as keyof typeof modalMap
      ] as ComponentType<ModalComponentProps>)
    : null;

  if (!activeModal || !modal || !ModalComponent) return null;

  const callback = (key?: string, data?: any) => {
    if (key) {
      dispatch(
        updateData({
          key,
          value: data,
        })
      );
    }

    modal.callback?.(data);
  };

  const handleHideModal = (data: any) => {
    dispatch(hideModal());
  };

  return (
    <div className="modal-container">
      <ModalComponent
        data={modal.modalData}
        onCallback={callback}
        hideModal={handleHideModal}
      />
    </div>
  );
};

export default ModalRoot;
